/**
 * AgentRedirectBanner
 *
 * Shown inline in a chat screen when the AI detects that the user's message
 * would be better handled by a different specialist agent.
 *
 * Props:
 *   agent       — agent key, e.g. "cps-advisor"
 *   message     — the original user message to carry over
 *   onDismiss   — called when the user taps "Stay here"
 *   onSwitch    — called with (route, message) when user taps "Switch"
 */
import { AGENT_INFO } from '@/utils/agentHandoff'

export default function AgentRedirectBanner({ agent, message, onDismiss, onSwitch }) {
  const info = AGENT_INFO[agent]
  if (!info) return null

  return (
    <div className="mx-1 rounded-2xl border border-brand-200 bg-brand-50 px-4 py-3 flex flex-col gap-3 animate-fade-in">
      {/* Header */}
      <div className="flex items-start gap-2.5">
        <div className="w-9 h-9 rounded-xl brand-gradient flex items-center justify-center text-white flex-shrink-0 text-base shadow-sm">
          {info.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-brand-800 leading-snug">
            Better match found
          </p>
          <p className="text-xs text-brand-600 mt-0.5 leading-relaxed">
            Your message looks like a{' '}
            <span className="font-semibold">{info.label}</span> question.
            Switch to get a more focused response.
          </p>
        </div>
        {/* Dismiss X */}
        <button
          onClick={onDismiss}
          className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-100 hover:bg-brand-200 flex items-center justify-center text-brand-500 transition-colors"
          aria-label="Dismiss"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onSwitch(info.route, message)}
          className="flex-1 py-2 px-3 rounded-xl brand-gradient text-white text-xs font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-transform shadow-sm"
        >
          <span>{info.icon}</span>
          Switch to {info.label}
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
        <button
          onClick={onDismiss}
          className="py-2 px-4 rounded-xl bg-white border border-slate-200 text-slate-600 text-xs font-semibold active:bg-slate-50 transition-colors"
        >
          Stay here
        </button>
      </div>
    </div>
  )
}
